import './SetupAuditTrail.scss';

import { monitorMenuConfigs } from './MonitorMenu';
import { SetupAuditTrailRule } from './SetupAuditTrailRule';
import { SonarText } from '@ui/text';
import { When } from 'react-if';

import { Alert, Button, Flex, Form, Select, Space } from 'antd';
import { COPY_SPECIFIC, LINKS } from '@sonar-software/sonar-ts-constants';
import { DataSecurityMonitorType, SetupAuditTrailMonitor } from '@sonar-software/sonar-core';
import React, { useMemo } from 'react';

export interface SetupAuditTrailProps {
	monitor: SetupAuditTrailMonitor
}

const conditionLogicOptions = [
	{
		label: COPY_SPECIFIC.ALL_CONDITIONS_ARE_MET,
		value: 'and'
	},
	{
		label: COPY_SPECIFIC.ANY_CONDITIONS_ARE_MET,
		value: 'or'
	}
];

export const SetupAuditTrail: React.FC<SetupAuditTrailProps> = ({
	monitor
}) => {
	const selectedRuleType = Form.useWatch([ 'monitorConfig', 'ruleType' ]);

	const initialRules = useMemo(() => {
		if (monitor.monitorConfig.rules.length > 0) {
			return monitor.monitorConfig.rules;
		}

		const configDefault = (monitorMenuConfigs.find(
			(x) => x.type === DataSecurityMonitorType.SetupAuditTrail
		)?.default as SetupAuditTrailMonitor).monitorConfig.rules;

		if (configDefault) {
			return configDefault;
		}

		return [];
	}, [ monitor.monitorConfig.rules ]);

	return (
		<Space
			className='setup-audit-trail-details'
			direction='vertical'>
			<Flex gap={ 24 }>
				<Flex
					gap={ 4 }
					justify='center'
					vertical>
					<SonarText className='subheading'>{ COPY_SPECIFIC.EVENT }</SonarText>
					<SonarText className='event-name'>{ COPY_SPECIFIC.SETUP_AUDIT_TRAIL_EVENT }</SonarText>
				</Flex>
				<Alert
					action={
						<a
							href={ LINKS.SETUP_AUDIT_TRAIL_HELP_ARTICLE }
							rel='noopener'
							target='_blank'>
							{ COPY_SPECIFIC.LEARN_MORE }
						</a>
					  }
					className='more-info-link'
					message={ COPY_SPECIFIC.TRACK_SETUP_AUDIT_TRAIL_ACTIVITY }
					showIcon
					type='info' />
			</Flex>
			<SonarText className='subheading'>{ COPY_SPECIFIC.CONDITION_LOGIC }</SonarText>
			<Form.Item
				className='condition-logic-selector'
				initialValue={ monitor.monitorConfig.ruleType }
				name={ [ 'monitorConfig', 'ruleType' ] }>
				<Select options={ conditionLogicOptions } />
			</Form.Item>
			<Form.List
				initialValue={ initialRules }
				name={ [ 'monitorConfig', 'rules' ] }>
				{ (fields, { add, remove }) =>
					<div className='rule-grid'>
						<When condition={ fields.length }>
							<div className='empty-grid-col' />
							<div>{ COPY_SPECIFIC.CONDITION }</div>
							<div>{ COPY_SPECIFIC.OPERATOR }</div>
							<div>{ COPY_SPECIFIC.VALUE }</div>
							<div className='empty-grid-col' />
						</When>
						{ fields.map((field) =>
							<SetupAuditTrailRule
								canDelete={ fields.length > 1 }
								field={ field }
								key={ field.key }
								onDelete={ () => remove(field.name) }
								ruleType={ selectedRuleType } />) }
						<div className='empty-grid-col' />
						<Button
							className='add-condition-button'
							onClick={ () => add() }>
							{ COPY_SPECIFIC.ADD_CONDITION }
						</Button>
					</div> }
			</Form.List>
		</Space>
	);
};
